import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    const saved = localStorage.getItem('agreenerside_cart');
    return saved ? JSON.parse(saved) : [];
  });
  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('agreenerside_cart', JSON.stringify(items));
  }, [items]);

  const addItem = (product, quantity = 1, selectedSize = null) => {
    setItems(prev => {
      const existingIndex = prev.findIndex(
        item => item.product_id === product.id && item.size === selectedSize
      );

      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex].quantity += quantity;
        toast.success(`Updated ${product.name} quantity`);
        return updated;
      }

      toast.success(`Added ${product.name} to cart`);
      return [...prev, {
        product_id: product.id,
        name: product.name,
        price: product.price,
        quantity,
        size: selectedSize,
        image: Array.isArray(product.image) ? product.image[0] : product.image
      }];
    });
  };

  const removeItem = (productId, size) => {
    setItems(prev => prev.filter(
      item => !(item.product_id === productId && item.size === size)
    ));
    toast.success('Item removed from cart');
  };

  const updateQuantity = (productId, size, quantity) => {
    if (quantity <= 0) {
      removeItem(productId, size);
      return;
    }
    setItems(prev => prev.map(item => 
      item.product_id === productId && item.size === size
        ? { ...item, quantity }
        : item
    ));
  };

  const clearCart = () => {
    setItems([]);
    localStorage.removeItem('agreenerside_cart');
  };

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  return (
    <CartContext.Provider value={{
      items,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      itemCount,
      subtotal,
      isCartOpen,
      setIsCartOpen
    }}>
      {children}
    </CartContext.Provider>
  );
};
